import type { ReactNode } from 'react';

export interface SettingsPanelProps {
  title: ReactNode;
  description?: ReactNode;
  /** Right-aligned control in the header band (e.g. an enable toggle). */
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}

/**
 * A settings panel: a light header band (title + description + optional action) above a
 * card body of <SettingsRow/> items. Mirrors common admin settings layouts.
 */
export function SettingsPanel({ title, description, action, children, className = '' }: SettingsPanelProps) {
  return (
    <div className={`card mb-3 ${className}`.trim()}>
      <div className="card-header bg-light border-bottom-0 d-flex align-items-start">
        <div>
          <h6 className="mb-0 fw-semibold">{title}</h6>
          {description && <div className="text-muted fs-sm mt-1">{description}</div>}
        </div>
        {action && <div className="ms-auto ps-3">{action}</div>}
      </div>
      <div className="card-body py-0">{children}</div>
    </div>
  );
}

export interface SettingsRowProps {
  title: ReactNode;
  description?: ReactNode;
  /** The control(s) for this setting. */
  children: ReactNode;
  className?: string;
}

/** One row in a SettingsPanel: label/description on the left, control on the right. */
export function SettingsRow({ title, description, children, className = '' }: SettingsRowProps) {
  return (
    <div className={`row py-3 border-top ${className}`.trim()}>
      <div className="col-lg-5">
        <div className="fw-semibold">{title}</div>
        {description && <div className="text-muted fs-sm mt-1">{description}</div>}
      </div>
      <div className="col-lg-7 mt-2 mt-lg-0">{children}</div>
    </div>
  );
}
